import type { OverlayEntry } from "./types";

/**
 * Индекс верхней blocking (modal) записи в стеке.
 * @param entries текущий overlay-стек
 * @returns индекс или `-1`, если blocking-записей нет
 */
export function getTopmostBlockingIndex(entries: Pick<OverlayEntry, "blocking">[]): number {
    return [...entries].map((entry) => entry.blocking).lastIndexOf(true);
}

/**
 * Проверяет, может ли surface принимать pointer/focus.
 * Записи ниже активной modal-surface считаются inert.
 */
export function isOverlayEntryInteractive(entryIndex: number, topmostBlockingIndex: number): boolean {
    if (topmostBlockingIndex === -1) {
        return true;
    }

    return entryIndex >= topmostBlockingIndex;
}

/**
 * Вычисляет `z-index` surface-слота.
 * Между surface-слотами оставляется место для modal backdrop текущей записи.
 * @param overlayBaseZIndex базовый `z-index` overlay-слоя из темы
 * @param entryIndex индекс записи в overlay-стеке
 * @returns `z-index` surface-элемента
 */
export function getOverlaySurfaceZIndex(overlayBaseZIndex: number, entryIndex: number): number {
    return overlayBaseZIndex + entryIndex * 2 + 1;
}

/**
 * Вычисляет `z-index` backdrop-а активной modal-записи.
 * Backdrop располагается выше всех нижних surfaces и ниже самой modal-surface.
 * @param overlayBaseZIndex базовый `z-index` overlay-слоя из темы
 * @param blockingEntryIndex индекс активной modal-записи
 * @returns `z-index` backdrop-элемента
 */
export function getOverlayBackdropZIndex(overlayBaseZIndex: number, blockingEntryIndex: number): number {
    return overlayBaseZIndex + blockingEntryIndex * 2;
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Возвращает видимые focusable-элементы внутри container.
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.getClientRects().length > 0,
    );
}

/**
 * Переносит фокус на первый focusable-элемент surface или на сам container.
 */
export function focusFirstFocusable(container: HTMLElement): void {
    const focusable = getFocusableElements(container);
    if (focusable.length === 0) {
        container.focus();
        return;
    }

    focusable[0].focus();
}

/**
 * Удерживает Tab/Shift+Tab внутри modal-surface.
 */
export function trapFocusInContainer(event: KeyboardEvent, container: HTMLElement): void {
    if (event.key !== "Tab") {
        return;
    }

    const focusable = getFocusableElements(container);
    if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
        if (active === first || (active instanceof Node && !container.contains(active))) {
            event.preventDefault();
            last.focus();
        }
        return;
    }

    if (active === last) {
        event.preventDefault();
        first.focus();
    }
}
